import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { ActivationCodeService } from '../../activation-code/services/activationCodeService';
import toast from 'react-hot-toast';

interface CreateCollectionBatchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  collectionId: string;
  collectionTitle: string;
}

const CreateCollectionBatchDialog: React.FC<CreateCollectionBatchDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  collectionId,
  collectionTitle
}) => {
  const [loading, setLoading] = useState(false);
  
  // Calculate default expiration date (5 years from now)
  const getDefaultExpiryDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 5);
    return date.toISOString().split('T')[0];
  };
  
  const [formData, setFormData] = useState({
    name: '',
    total_codes: 50,
    expires_at: getDefaultExpiryDate(),
    description: '',
    code_prefix: '',
    code_middle: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Tên lô mã là bắt buộc';
    }

    if (!formData.code_prefix.trim()) {
      newErrors.code_prefix = 'Tiền tố là bắt buộc';
    } else if (formData.code_prefix.length > 10) {
      newErrors.code_prefix = 'Tiền tố không được vượt quá 10 ký tự';
    }

    if (!formData.code_middle.trim()) {
      newErrors.code_middle = 'Trung tố là bắt buộc';
    } else if (formData.code_middle.length > 10) {
      newErrors.code_middle = 'Trung tố không được vượt quá 10 ký tự';
    }

    if (!formData.total_codes || formData.total_codes < 1) {
      newErrors.total_codes = 'Số lượng phải lớn hơn 0';
    }

    if (formData.total_codes > 10000) {
      newErrors.total_codes = 'Số lượng không được vượt quá 10,000';
    }

    if (!formData.expires_at) {
      newErrors.expires_at = 'Ngày hết hạn là bắt buộc';
    } else {
      const expiryDate = new Date(formData.expires_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (expiryDate <= today) {
        newErrors.expires_at = 'Ngày hết hạn phải sau ngày hôm nay';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      const batchData = {
        name: formData.name,
        collection_id: collectionId,
        collection_title: collectionTitle,
        total_codes: formData.total_codes,
        expires_at: formData.expires_at,
        code_prefix: formData.code_prefix,
        code_middle: formData.code_middle,
        sku: `${formData.code_prefix}-${formData.code_middle}` // Generate SKU from prefix and middle
      };

      // Call the API
      const response = await ActivationCodeService.createBatch(batchData);
      
      if (response.success) {
        onSuccess?.();
        onOpenChange(false);
        
        // Reset form
        setFormData({
          name: '',
          total_codes: 50,
          expires_at: getDefaultExpiryDate(),
          description: '',
          code_prefix: '',
          code_middle: ''
        });
        setErrors({});
        
        toast.success(`Đã tạo thành công ${formData.total_codes} mã kích hoạt cho bộ sách`);
      } else {
        throw new Error(response.message || 'Có lỗi xảy ra khi tạo lô mã');
      }
      
    } catch (error: any) {
      console.error('Error creating batch:', error);
      
      // Handle specific API errors
      if (error.response?.data?.message) {
        toast.error(`Lỗi: ${error.response.data.message}`);
      } else if (error.message) {
        toast.error(`Lỗi: ${error.message}`);
      } else {
        toast.error('Có lỗi xảy ra khi tạo lô mã');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Tạo mã kích hoạt cho Bộ sách</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Collection Info */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <p className="text-sm text-blue-900">
              <strong>Bộ sách:</strong> {collectionTitle}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Mã kích hoạt này sẽ áp dụng cho tất cả các sách trong bộ sách
            </p>
          </div>

          {/* Tên lô mã */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tên lô mã <span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="VD: Lô mã bộ sách Toán học"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className={`placeholder:text-gray-400 ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name}</p>
            )}
          </div>

          {/* Row với Tiền tố và Trung tố */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tiền tố mã <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="VD: COLL"
                value={formData.code_prefix}
                onChange={(e) => handleChange('code_prefix', e.target.value.toUpperCase())}
                className={`placeholder:text-gray-400 ${errors.code_prefix ? 'border-red-500' : ''}`}
                maxLength={10}
              />
              {errors.code_prefix && (
                <p className="text-red-500 text-sm mt-1">{errors.code_prefix}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Tối đa 10 ký tự</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trung tố mã <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="VD: 2024"
                value={formData.code_middle}
                onChange={(e) => handleChange('code_middle', e.target.value.toUpperCase())}
                className={`placeholder:text-gray-400 ${errors.code_middle ? 'border-red-500' : ''}`}
                maxLength={10}
              />
              {errors.code_middle && (
                <p className="text-red-500 text-sm mt-1">{errors.code_middle}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Tối đa 10 ký tự</p>
            </div>
          </div>

          {/* Row với Số lượng và Hạn sử dụng */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Số lượng mã <span className="text-red-500">*</span>
              </label>
              <Input
                type="number"
                min="1"
                max="10000"
                placeholder="50"
                value={formData.total_codes}
                onChange={(e) => handleChange('total_codes', parseInt(e.target.value) || 0)}
                className={`placeholder:text-gray-400 ${errors.total_codes ? 'border-red-500' : ''}`}
              />
              {errors.total_codes && (
                <p className="text-red-500 text-sm mt-1">{errors.total_codes}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">Tối đa 10,000 mã mỗi lô</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ngày hết hạn <span className="text-red-500">*</span>
              </label>
              <Input
                type="date"
                value={formData.expires_at}
                onChange={(e) => handleChange('expires_at', e.target.value)}
                className={`placeholder:text-gray-400 ${errors.expires_at ? 'border-red-500' : ''}`}
                min={new Date().toISOString().split('T')[0]}
              />
              {errors.expires_at && (
                <p className="text-red-500 text-sm mt-1">{errors.expires_at}</p>
              )}
            </div>
          </div>

          {/* Mô tả */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mô tả
            </label>
            <Textarea
              placeholder="Mô tả về mục đích sử dụng lô mã này..."
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              className="placeholder:text-gray-400"
            />
          </div>

          {/* Preview info */}
          {formData.code_prefix && formData.code_middle && (
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-medium text-green-900 mb-2">📋 Thông tin lô mã sẽ tạo:</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-green-700">Bộ sách:</span> {collectionTitle}
                </div>
                <div>
                  <span className="text-green-700">Số lượng:</span> {formData.total_codes} mã
                </div>
                <div>
                  <span className="text-green-700">Tên lô:</span> {formData.name || 'Chưa nhập'}
                </div>
                <div>
                  <span className="text-green-700">Format:</span> {formData.code_prefix}-{formData.code_middle}-XXXXXXXXX
                </div>
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Hủy
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Đang tạo...
                </span>
              ) : (
                '🚀 Tạo lô mã'
              )}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateCollectionBatchDialog;

